import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Dönüşümde ölçmenin önemi e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "benchmarking", "case study", "maturity", "ROI" gibi
 * yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookMeasurement: WrittenPage = {
  navLabel: 'Dönüşümde ölçme',
  title: 'The Importance of Measurement for Transformation Success',
  description:
    'Ölçülen iş yapılır: yapılandırılmış ölçme, dönüşümü niyetten sonuca nasıl taşıyor? Digitopia Impact Labs’in ölçme e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Ölçülen iş yapılır',
      headline: 'The Importance of Measurement for Transformation Success',
      body: [
        'Yapılandırılmış bir ölçmenin, dönüşümünüzü bir niyetten gerçek bir sonuca nasıl '
          + 'taşıdığını keşfedin.',
      ],
      ctas: [{ label: 'İndirin', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: siyah zeminde cam bir köstek saati; kadranı bir galaksi, '
          + 'çevresinde dönen altın yaylar.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      body: [
        'Dönüşüm tek seferlik bir program değil; şirketinizin yeni işletim sistemi. Yine de '
          + 'çoğu şirket değişim programlarını en çok ihtiyaç duyduğu şey olmadan başlatıyor: '
          + 'görünürlük. Bu e-kitap, dönüşümünüzün gerçekten işlemesini sağlayan en güçlü '
          + 'aracın neden ölçme olduğunu anlatıyor.',
        'Dijital, kültürel ya da sürdürülebilirlik tarafını yönetiyor olun; boşa giden emeği '
          + 'nasıl önleyeceğinizi, ekiplerinizi nasıl hizalayacağınızı ve yalnızca hareket '
          + 'etmek yerine nasıl bir yöne doğru ilerleyeceğinizi öğreneceksiniz.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-covers.webp',
        alt:
          'Ölçme e-kitabının iki kopyası, biri diğerinin arkasında, açık renkli bir zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      points: [
        'Dönüşüm çalışmalarının çoğu neden duruyor ve ölçme bu döngüyü nasıl kırıyor',
        'Maturity, yetkinlik ve sonuçları gerçek iş çıktılarına nasıl bağlayacağınız',
        'Stratejinizi keskinleştirmek için benchmarking’i ne zaman ve nasıl kullanacağınız',
        'Yönetim, ekipler ve fonksiyonlar arasında ortak bir dili nasıl kuracağınız',
        'Sürekli iyileşme neden sürekli ölçmeye bağlı',
        'Perakende, lojistik ve daha fazlasından gerçek case study’ler',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.measurementEbookPdf }],
      photo: {
        src: '/photo/measurement-transformation-ebook-stack.webp',
        alt: 'Yukarıdan görünen, üst üste dizilmiş ölçme e-kitabı kopyaları.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'İlerlemeyi görünür kılmaya hazır mısınız?',
      sub:
        'Ölçmenin dönüşümü stratejiden kültüre, oradan ROI’ye nasıl başarıya taşıdığını anlatan '
        + 'rehberin tamamını indirin.',
      cta: { label: 'E-kitabı indirin', href: LINKS.measurementEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Digitopia’nın Digital Maturity Index’i, dijital dönüşümümüzü yapılandırmanın temel '
            + 'unsurlarından biri oldu. Nerede durduğumuza ve hem holding içinde hem sektörde '
            + 'nasıl bir yerde olduğumuza dair net, nesnel bir görüş sağladı. Daha da önemlisi, '
            + 'ilerlememizi zaman içinde takip etmemizi ve dijital yatırımlarımızı ölçülebilir '
            + 'sonuçlarla hizalamamızı mümkün kıldı. Bu yapılandırılmış yaklaşım, dönüşüm '
            + 'çalışmalarımıza netlik, odak ve güven getiriyor.',
          source: 'Erdinç Uğurlu, CIO',
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
        },
      ],
    },
  ],
}
